import { datadogLogs } from "@datadog/browser-logs";
import markerSDK, { type MarkerSdk } from "@marker.io/browser";

import { viteEnv } from "utils/vite-env.util";

let widget: MarkerSdk | undefined;
const loadListeners = new Set<() => void>();

// Skipped on local unless a project id is set, so the widget can be run locally
// on purpose (the footer's Help row needs it) without every dev seeing it.
const shouldLoadWidget = () =>
  viteEnv.VITE_ENV !== "local" || Boolean(viteEnv.VITE_MARKER_PROJECT_ID);

export default async function setupMarkerIO() {
  if (shouldLoadWidget()) {
    try {
      widget = await markerSDK.loadWidget({
        project: viteEnv.VITE_MARKER_PROJECT_ID!
      });
      loadListeners.forEach((listener) => listener());
    } catch (error) {
      // The app works without the bug-report button. The loader rejects with
      // Marker.io's API error string ("You are not allowed to access this
      // project" when /widget/ping answers 403), and index.tsx does not await
      // this, so it surfaced as an uncaught error on the error monitor. Kept as
      // a warn so the rate is still visible.
      datadogLogs.logger.warn("Marker.io widget failed to load", {
        marker_io_error: String(error)
      });
    }
  }
}

/**
 * The loaded SDK, or `undefined` until it loads (and always on local, where the
 * widget is skipped). Pair with `subscribeToMarkerWidget` in `useSyncExternalStore`
 * so a component mounted before the widget arrives re-renders when it does.
 */
export const getMarkerWidget = () => widget;

export const subscribeToMarkerWidget = (listener: () => void) => {
  loadListeners.add(listener);
  return () => {
    loadListeners.delete(listener);
  };
};

export { widget };
